// Implements: F-IDN-017, F-UIX-009
/**
 * "Mis datos" (v3 `acctData`): download the JSON export, and the danger zone that erases the account
 * and the personal data. Pure presentation; the export failure comes back as `exportFailed`.
 */

import { useId } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import shared from "./account.module.css";

export interface PersonalDataPanelProps {
  readonly downloading: boolean;
  readonly exportFailed: boolean;
  readonly onDownload: () => void;
  readonly onErase: () => void;
}

export function PersonalDataPanel({ downloading, exportFailed, onDownload, onErase }: PersonalDataPanelProps): ReactElement {
  const { t } = useTranslation();
  const exportId = useId();
  const eraseId = useId();
  return (
    <>
      <section className={[shared.card, shared.spaced].join(" ")} aria-labelledby={exportId}>
        <div className={shared.head}>
          <h2 className={shared.title} id={exportId}>
            {t("account.data.exportTitle")}
          </h2>
        </div>
        <div className={shared.body}>
          <p className={shared.text}>{t("account.data.exportBody")}</p>
          {exportFailed ? (
            <div className={shared.noticeDanger} role="alert">
              <Icon name="alert-circle" size="sm" />
              <span>{t("account.data.exportFailed")}</span>
            </div>
          ) : null}
        </div>
        <div className={shared.foot}>
          <Button variant="secondary" loading={downloading} disabled={downloading} onClick={onDownload}>
            <Icon name="download" size="sm" />
            {t("account.data.download")}
          </Button>
        </div>
      </section>
      <section className={[shared.card, shared.danger].join(" ")} aria-labelledby={eraseId}>
        <div className={shared.head}>
          <h2 className={shared.dangerTitle} id={eraseId}>
            {t("account.data.eraseTitle")}
          </h2>
        </div>
        <div className={shared.body}>
          <p className={shared.text}>{t("account.data.eraseBody")}</p>
          <p className={shared.help}>{t("account.data.eraseHelp")}</p>
        </div>
        <div className={shared.foot}>
          <Button variant="danger" hasPopup="dialog" onClick={onErase}>
            <Icon name="trash" size="sm" />
            {t("account.data.erase")}
          </Button>
        </div>
      </section>
    </>
  );
}
